/**
 * The Message Center — notices from IT, on the home page.
 *
 * It carries the `HOME`-placement notices and nothing else. `CENTER` is the
 * demotion an admin reaches for when something is still true but no longer
 * worth the front page, and that demotion only means anything if this panel
 * genuinely refuses to show it. The full board is one click away via "View
 * all"; that link appears only when there is something to view.
 *
 * The help link stays pinned at the bottom, below whatever IT has published. It
 * is the one permanent item on the board — evergreen, never urgent, and it was
 * here before there was an API behind any of this.
 *
 * A failed or still-loading query renders as the panel's pre-API state: the
 * help link alone. A notice board that shows an error where a notice should be
 * is worse than one that quietly shows nothing, and this sits beside the
 * conversation rather than being the page.
 *
 * It renders only on the landing state — a notice board halfway down a live
 * conversation is noise.
 */

import { ArrowRight, Icon, Megaphone } from "@norican/icons";
import { cn, Typography } from "@norican/react";
import { Link } from "react-router";

import { MessageCard, useMessages } from "@/features/messages";
import { useT } from "@/i18n";

export interface MessageCentreProps {
  className?: string;
}

/** Most a landing page should carry. Beyond this, "View all" is the answer. */
const HOME_LIMIT = 3;

export function MessageCentre({ className }: MessageCentreProps) {
  const { t } = useT("assistant");
  const { t: tMessages } = useT("messages");

  const { data } = useMessages("HOME");
  const messages = data ?? [];

  return (
    <section className={cn("flex flex-col gap-2", className)} aria-label={t("messageCentre.title")}>
      <div className="flex items-center gap-2">
        <Icon icon={Megaphone} size="sm" tone="accent" />
        <Typography as="h2" variant="label" tone="muted" className="tracking-wide uppercase">
          {t("messageCentre.title")}
        </Typography>
        {messages.length === 0 ? null : (
          <Link
            to="/messages"
            className="text-accent-fg-subtle hover:text-accent-hover ml-auto flex items-center gap-1 text-xs font-medium"
          >
            {tMessages("viewAll")}
            <Icon icon={ArrowRight} size="xs" />
          </Link>
        )}
      </div>

      {messages.slice(0, HOME_LIMIT).map((message) => (
        <MessageCard key={message.message_id} message={message} compact />
      ))}
    </section>
  );
}
